import { FormikValues } from 'formik'
import { FC, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import * as yup from 'yup'
import { SimpleFormModal } from '~/components/SimpleFormModal/SimpleFormModal'
import { createAircraft, updateAircraft } from '~/modules/aircraft/data/aircraftApi'
import { IAircraft, initialRealStateAsset, IRealStateAsset } from '~/utils/types'
import {
  assetToRealStateAsset,
  findOrCreateCustomAssetManufacturer,
  findOrCreateCustomAssetModel,
  joinRealStateAssetTranslations,
} from '../functions'
import { RealStateAssetForm } from './RealStateAssetForm'

interface CustomAssetModalProps {
  values?: IAircraft // Asset translation from aircraft to company
  initialValuesAsCustom?: Partial<IRealStateAsset>
  creating: boolean
  open: boolean
  onClose: () => void
  onMutate?: (newItem: IAircraft) => void
}

export const realStateAssetValidationSchema = yup.object({
  suite_number: yup.string().required('Field required'),
  address_line: yup.string().required('Field required'),
  zip: yup.string().required('Field required'),
  property_name: yup.string().required('Field required'),
  city: yup.string().required('Field required'),
  state: yup.string().required('Field required'),
  extra_fields: yup.object({
    type: yup.string().oneOf(['COMMERCIAL', 'RESIDENTIAL', 'MIXED USE']),
  }),
})

export const RealStateAssetModal: FC<CustomAssetModalProps> = ({
  values,
  initialValuesAsCustom,
  open,
  onClose,
  creating = true,
  onMutate,
}) => {
  const { t } = useTranslation()

  const initialValues = useMemo((): IRealStateAsset => {
    return {
      ...(values === undefined ? initialRealStateAsset() : assetToRealStateAsset(values)),
      ...initialValuesAsCustom,
    }
  }, [initialValuesAsCustom, values])

  const onSubmit = useCallback(
    async (rawValues: FormikValues) => {
      const formikValues = rawValues as IRealStateAsset
      const initialTranslated = joinRealStateAssetTranslations(initialValues)
      const translatedValues = joinRealStateAssetTranslations(formikValues)

      if (creating) {
        const manufacturerId = await findOrCreateCustomAssetManufacturer(
          translatedValues.manufacturerName,
        )

        if (manufacturerId === null) return

        const modelId = await findOrCreateCustomAssetModel(
          translatedValues.assetModelName,
          manufacturerId,
          '681281cfab2c67d14ace26e7', // Real Estate asset type ID.
        )

        if (modelId === null) return

        const createdAsset = await createAircraft({
          aircraft_model: modelId,
          manufacturerId: manufacturerId,
          id_external_system: translatedValues.id_external_system,
          serial_number: translatedValues.serial_number,
          extra_fields: translatedValues.extra_fields,
        } as unknown as IAircraft)

        if (createdAsset) {
          onMutate?.(createdAsset)
          toast.success('Real Estate asset successfully created.')

          onClose()
        }

        return
      }

      const manufacturerId = await findOrCreateCustomAssetManufacturer(
        initialTranslated.manufacturerName,
      )

      if (manufacturerId === null) return

      const modelUpdated = initialTranslated.assetModelName !== translatedValues.assetModelName
      const manufacturerUpdated =
        initialTranslated.manufacturerName !== translatedValues.manufacturerName

      let newManufacturerId = null

      if (manufacturerUpdated) {
        newManufacturerId = await findOrCreateCustomAssetManufacturer(
          translatedValues.manufacturerName,
        )
      }

      let newModelId = null

      if (modelUpdated || newManufacturerId) {
        newModelId = await findOrCreateCustomAssetModel(
          translatedValues.assetModelName,
          newManufacturerId ?? manufacturerId,
          '681281cfab2c67d14ace26e7', // Real Estate asset type ID.
        )
      }

      const isUpdated =
        initialTranslated.serial_number !== translatedValues.serial_number ||
        initialTranslated.id_external_system !== translatedValues.id_external_system
      const extraFieldsUpdated =
        initialTranslated.extra_fields?.type !== translatedValues.extra_fields?.type
      if ((isUpdated || extraFieldsUpdated || newModelId) && values?._id) {
        await updateAircraft({
          aircraft_id: values?._id,
          aircraft: {
            serial_number: translatedValues.serial_number,
            audits: [],
            extra_fields: translatedValues.extra_fields,
            id_external_system: translatedValues.id_external_system,
            aircraft_model: newModelId ?? undefined,
          } as unknown as IAircraft,
        })
      }

      onMutate?.(values as IAircraft)
      toast.success('Real Estate asset successfully updated.')
      onClose()
    },
    [creating, initialValues, onClose, onMutate, values],
  )

  if (!open) return null

  return (
    <SimpleFormModal
      form={{
        initialValues: initialValues,
        onSubmit,
        validationSchema: realStateAssetValidationSchema,
      }}
      open={open}
      onClose={onClose}
      title={`${creating ? t('Creating') : t('Editing')} ${t('Real Estate Asset')}`}
    >
      <RealStateAssetForm />
    </SimpleFormModal>
  )
}
